import React, { useEffect, useState } from 'react'
import { Button, Form, Input, useTraversal } from '@guillotinaweb/react-gmi'
import { landingRedirectsPath } from '../../landingSlug'

async function readError(resp, fallback) {
  try {
    const body = await resp.json()
    return body.reason || body.message || fallback
  } catch (e) {
    return fallback
  }
}

export function Redirects() {
  const Ctx = useTraversal()
  const endpoint = landingRedirectsPath(Ctx.path)
  const [items, setItems] = useState([])
  const [publicPaths, setPublicPaths] = useState({})
  const [sources, setSources] = useState([''])
  const [destination, setDestination] = useState('')
  const [loading, setLoading] = useState(false)

  const defaultDestination =
    publicPaths.es || Object.values(publicPaths)[0] || ''

  async function load() {
    const resp = await Ctx.client.get(endpoint)
    if (resp.status !== 200) {
      Ctx.flash(await readError(resp, 'Error loading redirects'), 'danger')
      return
    }
    const data = await resp.json()
    setItems(data.items || [])
    setPublicPaths(data.public_paths || {})
    setDestination(
      (current) =>
        current ||
        data.public_paths?.es ||
        Object.values(data.public_paths || {})[0] ||
        ''
    )
  }

  useEffect(() => {
    load()
    // The landing path does not change while this tab is mounted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint])

  function resetForm() {
    setSources([''])
    setDestination(defaultDestination)
  }

  async function onSubmit() {
    const cleaned = sources.map((source) => source.trim()).filter(Boolean)
    if (!cleaned.length || !destination.trim()) {
      Ctx.flash('Source and destination are required', 'danger')
      return
    }
    setLoading(true)
    const resp = await Ctx.client.post(endpoint, {
      sources: cleaned,
      destination: destination.trim(),
    })
    setLoading(false)
    if (resp.status >= 400) {
      Ctx.flash(await readError(resp, 'Error creating the redirect'), 'danger')
      return
    }
    Ctx.flash('301 redirect saved', 'success')
    resetForm()
    await load()
  }

  async function remove(srcUrl) {
    const resp = await Ctx.client.delete(endpoint, { src_url: srcUrl })
    if (resp.status >= 400) {
      Ctx.flash(await readError(resp, 'Error removing the redirect'), 'danger')
      return
    }
    Ctx.flash('Redirect removed', 'success')
    await load()
  }

  return (
    <div className="landing-redirects">
      <h2 className="title is-size-5">Redirects</h2>
      {items.length === 0 ? (
        <p className="has-text-grey">No redirects point to this landing.</p>
      ) : (
        <ul className="landing-redirect-list">
          {items.map((item) => (
            <li key={item.src_url}>
              <code>{item.src_url}</code>
              <span>→</span>
              <code>{item.dst_path}</code>
              <span className="tag is-info is-light">301</span>
              <Button
                type="button"
                className="is-danger is-small is-outlined"
                onClick={() => remove(item.src_url)}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      <Form className="landing-redirect-form" onSubmit={onSubmit}>
        <h3 className="title is-size-6">Add redirection</h3>
        <label className="label">Source URLs</label>
        {sources.map((source, index) => (
          <div className="source-row" key={index}>
            <Input
              placeholder="/es/content/old-slug/"
              value={source}
              onChange={(value) =>
                setSources((current) =>
                  current.map((item, i) => (i === index ? value : item))
                )
              }
            />
            {sources.length > 1 && (
              <button
                type="button"
                className="landing-redirect-remove"
                onClick={() =>
                  setSources((current) => current.filter((_, i) => i !== index))
                }
              >
                Remove
              </button>
            )}
          </div>
        ))}
        <Button
          type="button"
          className="is-light"
          onClick={() => setSources((current) => [...current, ''])}
        >
          Add another
        </Button>

        <label className="label">Destination URL</label>
        <Input
          required
          placeholder="/es/content/new-slug/"
          value={destination}
          onChange={setDestination}
        />

        <p className="landing-redirect-type">301 Permanent Move</p>

        <div className="landing-redirect-actions">
          <Button type="button" className="is-light" onClick={resetForm}>
            Cancel
          </Button>
          <Button className="is-primary" loading={loading}>
            Add Redirection
          </Button>
        </div>
      </Form>
    </div>
  )
}
